/* Enabled roots, ALL vendors: all four exact-white corner pixels => contain; otherwise cover.
 * Not semantic image recognition. First visit may swap fit after analysis.
 * No geometry, source image, lightbox, or non-image media changes.
 */
(function (global, factory) {
  'use strict';
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else if (!global.NFDCornerFraming || global.NFDCornerFraming.VERSION !== api.VERSION) {
    global.NFDCornerFraming = api; api.start(global.document);
  }
}(typeof window !== 'undefined' ? window : this, function () {
  'use strict';
  var VERSION = 'nfd-exact-native-corners-all-vendors-v4';
  var PREFIX = VERSION + ':';
  var pending = new Map();
  var memoryCache = new Map();
  var queue = [];
  var running = 0;
  var activeRoots = new WeakSet();
  var observers = new WeakMap();
  var rootTokens = new WeakMap();

  function classifyCorners(data, width, height) {
    if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1 ||
        !data || data.length !== width * height * 4) return null;
    var corners = [0, (width-1)*4, (height-1)*width*4, (height*width-1)*4];
    return corners.every(function (i) {
      return data[i] === 255 && data[i+1] === 255 && data[i+2] === 255 && data[i+3] === 255;
    }) ? 'contain' : 'cover';
  }

  function classifyFourPixels(data) {
    if (!data || data.length !== 16) return null;
    for (var i=0; i<16; i++) if (data[i] !== 255) return 'cover';
    return 'contain';
  }

  // Vendor argument retained for compatibility; successful pixel decisions apply to ALL vendors.
  function resolveFit(vendor, mediaType, existing, result) {
    if (mediaType !== 'image') return existing;
    return result === 'contain' || result === 'cover' ? result : existing;
  }

  function sourceIdentity(src, base) {
    try {
      // Shopify raw image.src can be 'files/name.jpg', not a browser URL.
      if (typeof src !== 'string' || !/^(https?:\/\/|\/\/|\/cdn\/)/.test(src)) return null;
      var u = new URL(src, base);
      if (u.protocol !== 'https:' && u.protocol !== 'http:') return null;
      u.hash = '';
      return u.href;
    } catch (_) { return null; }
  }


  function originalSourceIdentity(src, base) {
    var identity = sourceIdentity(src, base);
    if (!identity) return null;
    var u = new URL(identity);
    // Liquid image_url supplies a valid CDN route/version. Remove its width
    // request so exact corner analysis never samples a resized derivative.
    u.searchParams.delete('width');
    return u.href;
  }

  function readCache(storage, identity) {
    try {
      var obj = JSON.parse(storage.getItem(PREFIX + identity));
      return obj && obj.version === VERSION && obj.source === identity &&
        (obj.fit === 'contain' || obj.fit === 'cover') ? obj.fit : null;
    } catch (_) { return null; }
  }

  function writeCache(storage, identity, fit) {
    if (fit !== 'contain' && fit !== 'cover') return;
    try { storage.setItem(PREFIX + identity, JSON.stringify({version:VERSION, source:identity, fit:fit})); }
    catch (_) { /* Quota/privacy failures affect speed, never framing fallback. */ }
  }

  function pump() {
    while (running < 2 && queue.length) {
      let item = queue.shift(); running++;
      Promise.resolve().then(item.work).then(item.resolve, function () { item.resolve(null); })
        .then(function () { running--; pump(); });
    }
  }
  function schedule(work) {
    return new Promise(function (resolve) { queue.push({work:work,resolve:resolve}); pump(); });
  }

  function sample(identity, win, registerAbort) {
    return new Promise(function (resolve) {
      var image = new win.Image();
      var finished = false;
      var timeout = win.setTimeout(function () { finish(null); }, 12000);
      function finish(fit) {
        if (finished) return; finished = true;
        win.clearTimeout(timeout); image.onload = image.onerror = null;
        image.src = ''; // Release the decoder/request on completion, timeout or last-consumer cancellation.
        resolve(fit);
      }
      registerAbort(function () { finish(null); });
      image.crossOrigin = 'anonymous';
      image.onload = function () {
        try {
          var w = image.naturalWidth, h = image.naturalHeight;
          if (!w || !h) return finish(null);
          // Copy four native pixels only. No image resizing/interpolation or patches.
          var canvas = win.document.createElement('canvas'); canvas.width=4; canvas.height=1;
          var ctx = canvas.getContext('2d', {willReadFrequently:true});
          if (!ctx) return finish(null);
          ctx.imageSmoothingEnabled = false;
          [[0,0],[w-1,0],[0,h-1],[w-1,h-1]].forEach(function (xy,index) {
            ctx.drawImage(image, xy[0], xy[1], 1, 1, index, 0, 1, 1);
          });
          finish(classifyFourPixels(ctx.getImageData(0, 0, 4, 1).data));
        } catch (_) { finish(null); } // CORS/tainted canvas/decode failure: unchanged.
      };
      image.onerror = function () { finish(null); };
      image.src = identity;
    });
  }

  function hasConsumers(job) {
    var alive = false;
    job.consumers.forEach(function (consumer) { if (consumer()) alive = true; });
    return alive;
  }
  function cancelUnused() {
    pending.forEach(function (job, identity) {
      if (!hasConsumers(job)) {
        pending.delete(identity);
        if (job.abort) job.abort();
      }
    });
  }
  function getFit(identity, win, storage, isAlive) {
    var cached = memoryCache.get(identity) || readCache(storage, identity);
    if (cached) return Promise.resolve(cached);
    var existing = pending.get(identity);
    if (existing) { existing.consumers.add(isAlive); return existing.promise; }
    var job = {consumers:new Set([isAlive]), abort:null, promise:null};
    job.promise = schedule(function () {
      if (!hasConsumers(job)) return null;
      return sample(identity, win, function (abort) { job.abort = abort; });
    }).then(function (fit) {
      if (fit) { memoryCache.set(identity, fit); writeCache(storage, identity, fit); }
      if (pending.get(identity) === job) pending.delete(identity);
      return fit;
    });
    pending.set(identity, job); return job.promise;
  }

  function initRoot(root) {
    if (root.dataset.nfdCornerFraming !== 'true') return;
    if (activeRoots && activeRoots.has(root)) return;
    if (activeRoots) activeRoots.add(root);
    var token = {active:true}; rootTokens.set(root, token);
    var vendor = root.dataset.nfdCornerVendor;
    var win = root.ownerDocument.defaultView;
    var storage = null;
    try { storage = win.localStorage; } catch (_) {}
    function processSlide(slide) {
      if (!root.isConnected || !slide.isConnected || root.dataset.nfdCornerFraming !== 'true') return;
      var image = slide.querySelector('img');
      if (!image) return;
      var identity = slide.dataset.nfdCornerSrc && originalSourceIdentity(slide.dataset.nfdCornerSrc, root.ownerDocument.baseURI);
      if (!identity) return;
      var original = slide.dataset.womenFit;
      getFit(identity, win, storage, function () { return token.active && root.isConnected && slide.isConnected && root.dataset.nfdCornerFraming === 'true'; }).then(function (fit) {
        if (!fit) return;
        // Theme editor removal/disable or source replacement must not apply stale work.
        if (!root.isConnected || !slide.isConnected || root.dataset.nfdCornerFraming !== 'true' ||
            originalSourceIdentity(slide.dataset.nfdCornerSrc, root.ownerDocument.baseURI) !== identity) return;
        slide.dataset.womenFit = resolveFit(vendor, 'image', original, fit);
      });
    }
    var slides = root.querySelectorAll('[data-media-type="image"][data-women-fit]');
    if (win.IntersectionObserver) {
      var observer = new win.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { observer.unobserve(entry.target); processSlide(entry.target); }
        });
      }, {rootMargin:'150px', threshold:0});
      observers.set(root, observer);
      slides.forEach(function (slide) { observer.observe(slide); });
    } // No observer support: preserve existing rather than eagerly decode the gallery.

  }
  function start(doc) {
    if (!doc) return;
    function scan(scope) {
      if (scope.matches && scope.matches('[data-nfd-corner-framing="true"]')) initRoot(scope);
      scope.querySelectorAll('[data-nfd-corner-framing="true"]').forEach(initRoot);
    }
    if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', function () { scan(doc); }, {once:true});
    else scan(doc);
    doc.addEventListener('shopify:section:load', function (event) { scan(event.target); });
    doc.addEventListener('shopify:section:unload', function (event) {
      var roots = Array.from(event.target.querySelectorAll('[data-nfd-corner-framing]'));
      if (event.target.matches('[data-nfd-corner-framing]')) roots.push(event.target);
      roots.forEach(function (root) {
        var token = rootTokens.get(root); if (token) token.active = false;
        var observer = observers.get(root); if (observer) observer.disconnect();
        observers.delete(root); activeRoots.delete(root);
        root.dataset.nfdCornerFraming = 'false';
      });
      cancelUnused();
    });
  }
  return {VERSION:VERSION, classifyCorners:classifyCorners, resolveFit:resolveFit,
    sourceIdentity:sourceIdentity, originalSourceIdentity:originalSourceIdentity, classifyFourPixels:classifyFourPixels, readCache:readCache,
    writeCache:writeCache, start:start};
}));

